import type { FormSchemaIssue } from '../form-utils/types';
import { track } from '../track';

export const VALIDATION_ERROR_EVENT = 'FORM/VALIDATION_ERROR';

export type ValidationErrorProperties = {
	field_name: string;
	error_type: string;
	form: string;
	step?: string | number;
};

/**
 * Derives a stable analytics category from a validation issue (zod's, or a
 * hand-written schema's using the same codes).
 *
 * The user-facing message is deliberately NOT used: it is display copy that
 * changes with wording tweaks and can be schema-authored to include the value
 * that was rejected. The issue code is a fixed zod taxonomy, so `error_type`
 * stays comparable over time and can never leak what the user typed.
 */
export const getErrorType = (issue: FormSchemaIssue) => {
	// `invalid_string` covers the formatting failures this event exists to
	// surface (zip, phone, email), so the subtype is what makes it actionable.
	if (issue.code === 'invalid_string') {
		return `invalid_string.${String(issue.validation)}`;
	}

	// zod 4 / zod mini renamed some codes. They are reported under their zod 3
	// names, so an `error_type` keeps meaning the same thing whichever zod the
	// form's schema is written with.
	if (issue.code === 'invalid_format') {
		return `invalid_string.${String(issue.format)}`;
	}

	if (issue.code === 'invalid_value') {
		// zod 4 reports a failed `z.literal` and a failed `z.enum` alike.
		return Array.isArray(issue.values) && issue.values.length === 1
			? 'invalid_literal'
			: 'invalid_enum_value';
	}

	return issue.code;
};

export const trackValidationError = ({
	field_name,
	error_type,
	form,
	step
}: ValidationErrorProperties) => {
	track(VALIDATION_ERROR_EVENT, {
		field_name,
		error_type,
		form,
		...(step === undefined ? {} : { step })
	});
};

/**
 * Reports a single field's validation error, collapsing repeats.
 *
 * Components re-render on every keystroke, so the same unchanged error would
 * otherwise fire an event per character. Passing `undefined` clears the field,
 * letting a later error report again — re-breaking a field the user had fixed
 * is real friction worth counting.
 */
export const createValidationErrorReporter = () => {
	let reportedErrorType: string | undefined;

	return (
		errorType: string | undefined,
		properties: Omit<ValidationErrorProperties, 'error_type'>
	) => {
		if (!errorType) {
			reportedErrorType = undefined;
			return;
		}

		if (reportedErrorType === errorType) {
			return;
		}

		reportedErrorType = errorType;
		trackValidationError({ ...properties, error_type: errorType });
	};
};
